import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Users, TrendingUp, Receipt, AlertTriangle,
  ArrowRight, BookOpen, TrendingDown, ChevronDown, ChevronUp, X,
} from 'lucide-react';
import { getDashboard } from '../api/dashboard';
import { getSoldProducts } from '../api/reports';
import { getDues } from '../api/reports';
import Modal from '../components/Modal';

function fmt(value) {
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(dt) {
  return new Date(dt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, iconBg, iconColor, onClick }) {
  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl p-5 ${onClick ? 'cursor-pointer hover:border-blue-300 transition-colors' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-xl ${iconBg}`}>
          <Icon size={18} className={iconColor} />
        </div>
      </div>
    </div>
  );
}

// ── Order Status Chips ────────────────────────────────────────────────────────

const getOrderStatusConfig = (t) => [
  { key: 'pending',   label: t('status.PENDING'),    style: 'bg-gray-100 text-gray-700' },
  { key: 'confirmed', label: t('status.CONFIRMED'),  style: 'bg-blue-50 text-blue-700' },
  { key: 'onTheWay',  label: t('status.ON_THE_WAY'), style: 'bg-purple-50 text-purple-700' },
  { key: 'delivered', label: t('status.DELIVERED'),  style: 'bg-green-50 text-green-700' },
  { key: 'cancelled', label: t('status.CANCELLED'),  style: 'bg-red-50 text-red-600' },
  { key: 'returned',  label: t('status.RETURNED'),   style: 'bg-orange-50 text-orange-600' },
];

const PAYMENT_STYLE = { PAID: 'bg-green-50 text-green-700', UNPAID: 'bg-amber-50 text-amber-700' };

// ── Dues Modal ────────────────────────────────────────────────────────────────

function DuesModal({ onClose }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(null);
  const { data: dues = [], isLoading } = useQuery({
    queryKey: ['dues'],
    queryFn: () => getDues().then((r) => r.data.data),
  });

  return (
    <Modal title={t('dashboard.outstandingDues')} onClose={onClose} wide>
      {isLoading ? (
        <p className="text-sm text-gray-400 text-center py-8">{t('common.loading')}</p>
      ) : dues.length === 0 ? (
        <p className="text-sm text-green-600 text-center py-8 font-medium">{t('dashboard.noDues')}</p>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {dues.map((row) => (
            <div key={row.customer.id} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(expanded === row.customer.id ? null : row.customer.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-800">{row.customer.name}</p>
                    <p className="text-xs text-gray-400">{row.customer.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{t('dashboard.unpaidAmount')}</p>
                    <p className="text-sm font-semibold text-red-600">৳{fmt(row.unpaidAmount)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{t('dashboard.paidAmount')}</p>
                    <p className="text-sm font-semibold text-green-600">৳{fmt(row.paidAmount)}</p>
                  </div>
                  {expanded === row.customer.id ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                </div>
              </button>
              {expanded === row.customer.id && (
                <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-3">
                  {row.unpaidOrders.map((order) => (
                    <div key={order.orderId}>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Order #{order.orderId} — ৳{fmt(order.totalAmount)}</p>
                      <div className="space-y-0.5">
                        {order.items.map((item) => (
                          <div key={item.id} className="flex justify-between text-xs text-gray-500">
                            <span>{item.productTitle} × {item.quantity}</span>
                            <span>৳{fmt(item.subtotal)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── Sold Products Table ───────────────────────────────────────────────────────

function SoldProductsTable() {
  const { t } = useTranslation();
  const { data = [], isLoading } = useQuery({
    queryKey: ['sold-products'],
    queryFn: () => getSoldProducts().then((r) => r.data.data),
  });

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <p className="text-sm font-semibold text-gray-700">{t('dashboard.productSales')}</p>
        <p className="text-xs text-gray-400 mt-0.5">{t('dashboard.salesFromDelivered')}</p>
      </div>
      {isLoading ? (
        <div className="py-10 text-center text-sm text-gray-400">{t('common.loading')}</div>
      ) : data.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-400">{t('dashboard.noDeliveredOrders')}</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 text-left">
              <th className="px-4 py-2.5 font-medium text-gray-500 text-xs">{t('common.product')}</th>
              <th className="px-4 py-2.5 font-medium text-gray-500 text-xs text-right">{t('dashboard.unitsSold')}</th>
              <th className="px-4 py-2.5 font-medium text-gray-500 text-xs text-right">{t('common.revenue')}</th>
              <th className="px-4 py-2.5 font-medium text-gray-500 text-xs text-right">{t('common.cost')}</th>
              <th className="px-4 py-2.5 font-medium text-gray-500 text-xs text-right">{t('common.profit')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((row) => (
              <tr key={row.productId} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-2.5 text-gray-800 font-medium text-xs">{row.productTitle}</td>
                <td className="px-4 py-2.5 text-right text-gray-600 text-xs">{row.unitsSold}</td>
                <td className="px-4 py-2.5 text-right text-gray-800 text-xs">৳{fmt(row.revenue)}</td>
                <td className="px-4 py-2.5 text-right text-gray-500 text-xs">৳{fmt(row.sourcingCost)}</td>
                <td className={`px-4 py-2.5 text-right font-semibold text-xs ${row.profit >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  ৳{fmt(row.profit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showDues, setShowDues] = useState(false);

  usePageTitle('Dashboard');
  const ORDER_STATUS_CONFIG = getOrderStatusConfig(t);
  const { data: raw, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => getDashboard().then((r) => r.data.data),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <p className="text-sm text-gray-400">{t('dashboard.loading')}</p>
      </div>
    );
  }

  const { orders, revenue, customers, expenses, inventory, recentOrders, stockAlerts } = raw;

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      {/* Page title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-sm text-gray-400 mt-0.5">{t('dashboard.subtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/guide')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 border border-gray-200 hover:border-blue-200 px-3 py-2 rounded-lg transition-colors"
        >
          <BookOpen size={14} />
          {t('dashboard.howToUse')}
        </button>
      </div>

      {/* Top stat cards — 5 cards: 3+2 grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={ShoppingCart} label={t('dashboard.totalOrders')} value={orders.total}
          sub={t('dashboard.onTheWaySub', { count: orders.onTheWay })} iconBg="bg-blue-50" iconColor="text-blue-600" />
        <StatCard icon={TrendingUp} label={t('dashboard.revenueDelivered')} value={`৳${fmt(revenue.totalDelivered)}`}
          sub={revenue.unpaidAmount > 0 ? `৳${fmt(revenue.unpaidAmount)} ${t('dashboard.unpaidTap')}` : t('dashboard.allCollected')}
          iconBg="bg-green-50" iconColor="text-green-600"
          onClick={revenue.unpaidAmount > 0 ? () => setShowDues(true) : undefined} />
        <StatCard icon={TrendingDown} label={t('dashboard.netProfit')}
          value={`৳${fmt(revenue.netProfit)}`}
          sub={t('dashboard.profitFormula')}
          iconBg={revenue.netProfit >= 0 ? 'bg-teal-50' : 'bg-red-50'}
          iconColor={revenue.netProfit >= 0 ? 'text-teal-600' : 'text-red-500'} />
        <StatCard icon={Receipt} label={t('dashboard.totalExpenses')} value={`৳${fmt(expenses.total)}`}
          iconBg="bg-red-50" iconColor="text-red-500" />
        <StatCard icon={Users} label={t('dashboard.customers')} value={customers.total}
          sub={t('dashboard.outOfStockSub', { count: inventory.outOfStock })} iconBg="bg-purple-50" iconColor="text-purple-600" />
      </div>

      {/* Order status breakdown */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <p className="text-sm font-semibold text-gray-700 mb-4">{t('dashboard.ordersByStatus')}</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {ORDER_STATUS_CONFIG.map(({ key, label, style }) => (
            <div key={key} className={`rounded-xl px-3 py-3 text-center ${style}`}>
              <p className="text-xl font-bold">{orders[key] ?? 0}</p>
              <p className="text-xs font-medium mt-0.5 opacity-80">{label}</p>
            </div>
          ))}
        </div>
        {revenue.unpaidAmount > 0 && (
          <button
            onClick={() => setShowDues(true)}
            className="mt-4 w-full flex items-center gap-2 bg-amber-50 border border-amber-200 hover:border-amber-400 rounded-lg px-4 py-2.5 transition-colors"
          >
            <AlertTriangle size={14} className="text-amber-500 shrink-0" />
            <p className="text-sm text-amber-800 flex-1 text-left">
              <span className="font-semibold">৳{fmt(revenue.unpaidAmount)}</span> {t('dashboard.unpaidAcrossOrders')}
            </p>
            <ArrowRight size={13} className="text-amber-500" />
          </button>
        )}
      </div>

      {/* Bottom two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Orders */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-700">{t('dashboard.recentOrders')}</p>
            <button onClick={() => navigate('/orders')} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
              {t('common.viewAll')} <ArrowRight size={12} />
            </button>
          </div>
          {recentOrders.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">{t('dashboard.noOrdersYet')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left">
                  <th className="px-4 py-2.5 font-medium text-gray-500 text-xs">#</th>
                  <th className="px-4 py-2.5 font-medium text-gray-500 text-xs">{t('common.customer')}</th>
                  <th className="px-4 py-2.5 font-medium text-gray-500 text-xs text-right">{t('common.total')}</th>
                  <th className="px-4 py-2.5 font-medium text-gray-500 text-xs">{t('common.status')}</th>
                  <th className="px-4 py-2.5 font-medium text-gray-500 text-xs">{t('common.payment')}</th>
                  <th className="px-4 py-2.5 font-medium text-gray-500 text-xs">{t('common.date')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentOrders.map((order) => (
                  <tr key={order.id} onClick={() => navigate('/orders')} className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3 text-gray-400 font-mono text-xs">#{order.id}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800 text-xs">{order.customerName}</p>
                      <p className="text-xs text-gray-400">{order.customerPhone}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-800 text-xs">৳{fmt(order.totalAmount)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                        ORDER_STATUS_CONFIG.find(s => s.key === order.status.toLowerCase().replace(/_/g, ''))?.style ?? 'bg-gray-100 text-gray-600'
                      }`}>
                        {t(`status.${order.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${PAYMENT_STYLE[order.paymentStatus]}`}>
                        {t(`status.${order.paymentStatus}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(order.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Stock Alerts */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-700">{t('dashboard.stockAlerts')}</p>
            <button onClick={() => navigate('/inventory')} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
              {t('common.viewAll')} <ArrowRight size={12} />
            </button>
          </div>
          {stockAlerts.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-green-600 font-medium">{t('dashboard.allStockedUp')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('dashboard.noLowStock')}</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {stockAlerts.map((product) => (
                <li key={product.id} className="flex items-center justify-between px-5 py-3">
                  <p className="text-sm text-gray-700 truncate pr-4">{product.title}</p>
                  {product.stockQuantity === 0
                    ? <span className="shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600">{t('dashboard.out')}</span>
                    : <span className="shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">{product.stockQuantity} {t('dashboard.left')}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Sold Products table */}
      <SoldProductsTable />

      {/* Dues Modal */}
      {showDues && <DuesModal onClose={() => setShowDues(false)} />}
    </div>
  );
}
